import { cloneElement, useState, useEffect } from 'react';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  withStyles
} from '@material-ui/core';
import { Formik, Form } from 'formik';

import CustomDialogTitle from '../../../partials/customDialogTitle/CustomDialogTitle';
import DraggableDialog from '../../../partials/DraggableDialog';
import styles from '../styles/changeSubscription';
import { translate } from '../../../i18n/I18nProvider';
import getSubscriptionDetail from '../actions/getSubscriptionDetails';
import { isDefined, isArray } from '../../../utils/isDefined';
import PrimaryButton from '../../../partials/customButtons/PrimaryButton';
import SecondaryButton from '../../../partials/customButtons/SecondaryButton';
import DataInputBox from '../../../partials/dataBox/DataInputBox';
import updateUserInfo from '../actions/updateUserInfo';

const EditUserInfo = ({
  subscription,
  clientId,
  children,
  classes,
  closeMenu,
  setRefresh
}) => {
  const [open, setOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onOpenDialog = () => setOpen(true);
  const onCloseDialog = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    closeMenu();
    (async function() {
      setIsLoading(true);
      const subDetail = await getSubscriptionDetail({
        clientId,
        externalReference: subscription?.externalReference
      });

      if (isDefined(subDetail?.userInfo)) {
        const { mappedInfo, error } = mapUserInfo(subDetail.userInfo);
        if (isDefined(error)) {
          setError(error);
          setIsLoading(false);
          return;
        }
        setUserInfo(mappedInfo);
      }
      setIsLoading(false);
    })();

    // eslint-disable-next-line
  }, [clientId, open, subscription]);

  const onSaveClick = async values => {
    setError(null);
    setIsLoading(true);
    const { editComplete, error } = await updateUserInfo({
      clientId,
      userInfo: values
    });

    if (isDefined(error)) {
      setError(error);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);

    if (editComplete) {
      onCloseDialog();
      setRefresh(true);
    }
  };

  return (
    <>
      {cloneElement(children, { onClick: onOpenDialog })}
      <Dialog
        maxWidth="lg"
        classes={{
          paper: classes.root
        }}
        PaperComponent={DraggableDialog}
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onCloseDialog();
          }
        }}
      >
        <CustomDialogTitle onCloseClick={onCloseDialog}>
          {translate('TOOLTIP.EDIT_USER_INFO')}
        </CustomDialogTitle>
        {isLoading && (
          <div className={classes.loaderOverlay}>
            <CircularProgress />
          </div>
        )}
        <DialogContent className={classes.editUserDialogContent}>
          {error && <div className={classes.error}>{error}</div>}
          <Formik
            initialValues={{ ...userInfo }}
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={onSaveClick}
          >
            {({
              handleSubmit,
              values,
              handleBlur,
              handleChange,
              errors,
              dirty
            }) => {
              const fieldProps = { errors, values, handleBlur, handleChange };
              return (
                <Form className={classes.content} onSubmit={handleSubmit}>
                  <DataInputBox
                    label={translate('NAME')}
                    name="navn"
                    width={280}
                    {...fieldProps}
                  />
                  <DataInputBox
                    label={translate('DEPARTMENT')}
                    name="afdeling"
                    width={280}
                    {...fieldProps}
                  />
                  <DataInputBox
                    label={translate('REMARK')}
                    name="bemrkning"
                    width={280}
                    {...fieldProps}
                  />
                  <DataInputBox
                    label={translate('REQUEST_NEW_USER.COST_CENTER')}
                    name="cost_center"
                    width={280}
                    {...fieldProps}
                  />

                  <DialogActions className={classes.footer}>
                    <SecondaryButton onClick={onCloseDialog}>
                      {translate('BUTTON.CANCEL')}
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSubmit} disabled={!dirty}>
                      {translate('BUTTON.SAVE_CLOSE')}
                    </PrimaryButton>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default withStyles(styles)(EditUserInfo);

const mapUserInfo = userInfo => {
  const { phoneNumber } = userInfo;

  const features = userInfo?.featureAttributes || [];

  if (!isArray(features)) {
    return { error: 'User info is not available' };
  }

  const mappedUserInfo = features.reduce((obj, feature) => {
    if (!feature.key.includes('-')) {
      return {
        ...obj,
        [feature.key]: feature.value
      };
    }

    const label = feature.key
      .split('-')
      .slice(2)
      .join('_');

    return {
      ...obj,
      [label]: feature.value
    };
  }, {});

  return { mappedInfo: { ...mappedUserInfo, phoneNumber } };
};
